import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api, type User } from '@/lib/api';
import { log } from '@/lib/log';
import { useWatchTogetherStore } from '@/stores/watchTogether';

// Auth store. Hydrates from `/api/auth/whoami` on app boot and after login;
// the store is the single source of truth for "who am I". The auth_token
// cookie remains the actual auth credential — we never store the JWT in
// localStorage (the legacy SPA did; we drop it for the new app).

export const useAuthStore = defineStore('auth', () => {
    const user = ref<User | null>(null);
    const loaded = ref(false);
    const loading = ref(false);

    const isAuthed = computed(() => user.value !== null);
    const isAdmin = computed(() => user.value?.role === 'admin');

    async function refresh() {
        loading.value = true;
        try {
            user.value = await api.auth.whoami();
            log.resumeTransport();
            log.debug('auth.refresh', { user: user.value?.username ?? null });
        } catch (err) {
            user.value = null;
            log.warn('auth.refresh_failed', { error: (err as Error).message });
        } finally {
            loading.value = false;
            loaded.value = true;
        }
    }

    async function login(username: string, password: string) {
        const r = await api.auth.login(username, password);
        user.value = r.user;
        log.resumeTransport();
        log.info('auth.login_ok', { username: r.user.username });
        return r.user;
    }

    async function logout() {
        // Close EventSource while the cookie still exists. A bounded server
        // recheck covers tabs that disappear before this client action runs.
        useWatchTogetherStore().stop();
        try {
            await api.auth.logout();
        } catch (err) {
            log.warn('auth.logout_request_failed', { error: (err as Error).message });
        }
        user.value = null;
        try {
            localStorage.removeItem('authToken');
        } catch {
            // ignore
        }
        log.info('auth.logout');
    }

    return { user, loaded, loading, isAuthed, isAdmin, refresh, login, logout };
});
